<template>
  <div>
    <div v-if="!isUpdate" class="mb-4 text-right">
      <BaseButton
        v-if="!isAddingCompany"
        text="Switch to a Company"
        @click="changeToAddCompany"
      />
      <BaseButton
        v-else
        text="Switch to a Person"
        @click="changeToAddCompany"
      />
    </div>

    <form
      @submit.stop.prevent="form.handleSubmit()"
    >
      <form.Field
        name="name"
        :validators="{
          onBlur: yup.string().required('Name is required'),
        }"
      >
        <template #default="{ field }">
          <BaseInput
            v-model="(field.state.value as string)"
            :name="field.name"
            type="text"
            label="Name"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        name="email"
        :validators="{
          onBlur: yup.string().required('Email is required').email('Email is invalid'),
        }"
      >
        <template #default="{ field }">
          <BaseInput
            v-model="(field.state.value as string)"
            :name="field.name"
            type="email"
            label="Email"

            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field
        name="phone"
        :validators="{
          onChange: yup.string().optional().matches(/^[0-9() -]*$/, 'Phone number is invalid'),
        }"
      >
        <template #default="{ field }">
          <BaseInputPhone
            v-model="(field.state.value as string)"
            :name="field.name"
            label="Phone"
            :error="field.state.meta.errors && field.state.meta.errors[0] as string"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
            @change="(e) => e !== field.state.value && field.handleChange(e)"
          />
        </template>
      </form.Field>

      <form.Field
        v-if="!isAddingCompany"
        name="companyId"
      >
        <template #default="{ field }">
          <BaseSelect
            v-model="(field.state.value as string)"
            :name="field.name"
            label="Company"
            :options="companiesOptions"
            @blur="field.handleBlur"
            @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
          />
        </template>
      </form.Field>

      <form.Field name="address">
        <template #default="{ field }">
          <AddressAutoComplete
            :name="field.name"
            :value="(field.state.value as string)"
            label="Address"
            @blur="field.handleBlur"
            @input="(e) => field.handleChange(e)"
          />
        </template>
      </form.Field>

      <BaseCheckbox
        v-model="billingAdressExists"
        name="billingAdressExists"
        label="Different Billing Address?"
      />

      <form.Field
        v-if="billingAdressExists"
        name="billing"
      >
        <template #default="{ field }">
          <AddressAutoComplete
            :name="field.name"
            :value="(field.state.value as string)"
            label="Billing Address"
            @blur="field.handleBlur"
            @input="(e) => { setBillingAdress(e); return field.handleChange(e)}"
          />
        </template>
      </form.Field>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              text="Cancel"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
              @click.prevent="emit('cancel', $event)"
            />
            <BaseButton
              type="submit"
              variant="primary"
              text="Save"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
              @click="emit('submit', $event)"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, PropType } from 'vue'
import { Customer } from '@/models/customer'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseInputPhone from '@/components/base/BaseInputPhone.vue'
import AddressAutoComplete from '@/components/base/AddressAutoComplete.vue'
import CustomerService from '@/services/CustomerService'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'

const props = defineProps({
  model: {
    type: Object as PropType<Customer>,
    default: () => ({})
  },
  saveFunction: {
    type: Function,
    required: true
  },
  isUpdate: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['cancel', 'submit'])
const customer = ref({ ...props.model })
const billingAdressExists = ref(customer.value.billing ? true : false)
const isAddingCompany = ref(props.isUpdate ? customer.value.isCompany : false)
const customerService = new CustomerService()

const companiesOptions = ref([])
customerService.getCompanies().then((data) => {
  companiesOptions.value = data.map((company) => ({
    value: company.id,
    label: company.name
  }))
})

const form = useForm({
  defaultValues: customer.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    if (isAddingCompany.value) {
      //remove undefined values
      const company = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined))  as unknown as Customer
      company.isCompany = true
      if (company.companyId) delete company.companyId

      await props.saveFunction(company)

    } else {
      // remove undefined values
      const customer = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as Customer
      customer.isCompany = false

      await props.saveFunction(customer)
    }
  }
})

const changeToAddCompany = () => {
  isAddingCompany.value = !isAddingCompany.value
}

const setBillingAdress = (value) => {
  if (value) {
    form.useStore((state) => state.values.billing = value)
  } else {
    form.useStore((state) => state.values.billing = '')
  }
}

watch(billingAdressExists, (newVal) => {
  if (newVal === false) {
    setBillingAdress('')
  }
})
</script>
